import { useCallback, useRef, useState } from 'react';
import { Play } from 'lucide-react';

// Cache-bust whenever the native master or VO is redeployed.
const BRIEFING_ASSET_REV = '20260809-lifecycle-contactable-bright';
const BRIEFING_VIDEO_SRC = `/videos/eigen-executive-briefing.mp4?v=${BRIEFING_ASSET_REV}`;
const BRIEFING_POSTER_SRC = `/images/eigen-executive-briefing-poster.jpg?v=${BRIEFING_ASSET_REV}`;

const shellClass = (isDark) =>
  isDark
    ? 'border-indigo-900/50 bg-[#0a0c14] shadow-[0_20px_50px_rgba(0,0,0,0.4)]'
    : 'border-indigo-200/80 bg-[#f8f9fc] shadow-[0_18px_40px_rgba(79,70,229,0.08)]';

/**
 * Executive briefing player — hero variant sits beside headline (primary placement).
 * Long-form narrated walkthrough; click-to-play, no autoplay.
 */
const EigenLeadsExecutiveBriefingPlayer = ({ isDark, variant = 'hero', className = '' }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isHero = variant === 'hero';

  const startPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    setIsPlaying(true);
    video.play().catch(() => {
      setIsPlaying(false);
    });
  }, []);

  return (
    <figure id="executive-briefing" className={`scroll-mt-24 ${className}`}>
      {isHero && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span
            className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            Executive Briefing
          </span>
          <span className={`text-[11px] ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>·</span>
          <span
            className={`text-[11px] font-medium tracking-wide ${
              isDark ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            ~2 min narrated walkthrough
          </span>
          <span className={`text-[11px] ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>·</span>
          <span
            className={`text-[11px] font-medium tracking-wide ${
              isDark ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            No registration
          </span>
        </div>
      )}

      <div className={`relative overflow-hidden rounded-xl border aspect-video ${shellClass(isDark)}`}>
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-px ${
            isDark
              ? 'bg-gradient-to-r from-transparent via-indigo-500/60 to-transparent'
              : 'bg-gradient-to-r from-transparent via-indigo-600/40 to-transparent'
          }`}
        />

        {!isPlaying && (
          <div
            className={`absolute left-3 top-3 z-[3] rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-sm ${
              isDark ? 'bg-black/55 text-slate-200 border border-white/10' : 'bg-white/90 text-slate-700 border border-slate-200/80'
            }`}
          >
            ~2 min
          </div>
        )}

        <video
          ref={videoRef}
          className="absolute inset-0 z-0 w-full h-full object-contain bg-black"
          controls={isPlaying}
          playsInline
          preload="none"
          poster={BRIEFING_POSTER_SRC}
          onPlay={() => setIsPlaying(true)}
          aria-label="eigenLeads executive briefing — high-quality contactable leads with live-pass release"
        >
          <source src={BRIEFING_VIDEO_SRC} type="video/mp4" />
        </video>

        {!isPlaying && (
          <>
            <img
              src={BRIEFING_POSTER_SRC}
              alt=""
              className="absolute inset-0 z-[1] w-full h-full object-contain bg-black"
              fetchPriority="high"
            />
            <button
              type="button"
              onClick={startPlayback}
              className={`absolute inset-0 z-[2] flex flex-col items-center justify-center gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                isHero
                  ? 'bg-gradient-to-t from-black/55 via-black/25 to-black/15 hover:from-black/60 hover:via-black/30'
                  : 'bg-black/35 hover:bg-black/45'
              }`}
              aria-label="Play executive briefing"
            >
              <span
                className={`flex items-center justify-center rounded-full border text-white backdrop-blur-sm transition-transform hover:scale-[1.03] ${
                  isHero
                    ? 'h-14 w-14 border-white/25 bg-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.35)]'
                    : 'h-16 w-16 bg-indigo-600 border-transparent shadow-lg shadow-indigo-900/40'
                }`}
              >
                <Play className={`fill-current ${isHero ? 'w-6 h-6 ml-0.5' : 'w-7 h-7 ml-0.5'}`} aria-hidden />
              </span>
              {isHero && (
                <span className="text-center px-6">
                  <span className="block text-sm font-semibold tracking-wide text-white drop-shadow-md">
                    Play executive briefing
                  </span>
                  <span className="block mt-1 text-xs font-medium text-slate-300/90 tracking-wide">
                    Verified · Contactable · Live-pass quality
                  </span>
                </span>
              )}
              {!isHero && (
                <span className="text-sm font-semibold tracking-wide text-white drop-shadow-md">
                  Watch the briefing
                </span>
              )}
            </button>
          </>
        )}
      </div>

      <figcaption
        className={`mt-3 text-[11px] uppercase tracking-[0.2em] leading-snug ${
          isHero ? 'text-left' : 'text-center'
        } ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
      >
        {isHero
          ? 'Executive eigenLeads briefing — verified, contactable leads to bound outcomes'
          : 'Customer Acquisition Bundles with capacity-aware execution and source-to-outcome economics'}
      </figcaption>
    </figure>
  );
};

export default EigenLeadsExecutiveBriefingPlayer;
